import { useMemo } from 'react';
import { ChatMessage } from '../../hooks/types';

const RICH_PAYLOAD_TYPES = ['location', 'contact', 'pix', 'poll'] as const;
const MEDIA_TYPES = ['image', 'video', 'audio', 'sticker', 'document', 'location', 'contact'] as const;

const IGNORED_MEDIA_LABELS = [
    '🎵 áudio',
    '🎤 áudio',
    '📷 foto',
    '📷 imagem',
    '📷 mídia',
    '🎬 vídeo',
    '🎥 vídeo',
    '📄 documento',
    '📄 arquivo',
    '🎭 figurinha',
    '📍 localização',
    '👤 contato',
    '📊 enquete',
];

function isRawMediaPayload(text: string, type: string): boolean {
    const trimmed = text.trim();
    if (trimmed.startsWith('{') && trimmed.includes('URL')) return true;
    if (trimmed.startsWith('https://mmg.whatsapp.net/')) return true;
    if (trimmed.startsWith('https://pps.whatsapp.net/')) return true;
    if (trimmed.toLowerCase() === type) return true;
    if (IGNORED_MEDIA_LABELS.includes(trimmed.toLowerCase())) return true;
    return false;
}

/** Texto exibível da mensagem (legenda ou corpo de texto). */
export function getMessageCaption(msg: ChatMessage, isMe: boolean, forDisplay = false): string | null {
    if (RICH_PAYLOAD_TYPES.includes(msg.type as (typeof RICH_PAYLOAD_TYPES)[number])) {
        return null;
    }

    if (msg.type === 'document') {
        if (!msg.text) return null;
        if (msg.text.trim().startsWith('{')) {
            try {
                const parsed = JSON.parse(msg.text);
                return parsed.caption || null;
            } catch {
                return null;
            }
        }
        return null;
    }

    if (msg.text && MEDIA_TYPES.includes(msg.type as (typeof MEDIA_TYPES)[number])) {
        if (isRawMediaPayload(msg.text, msg.type)) return null;
    }

    if (forDisplay && isMe && msg.text) {
        const agentNamePattern = /^\*([^*\n]+)\*\s*/;
        const match = msg.text.match(agentNamePattern);
        if (match) {
            return msg.text.replace(agentNamePattern, `*$1*:\n`);
        }
    }

    return msg.text ?? null;
}

/** Mensagens próprias editáveis: texto puro ou mídia com legenda. */
export function isMessageEditable(message: ChatMessage): boolean {
    if (message.sender !== 'me') return false;
    const caption = getMessageCaption(message, true, false);
    if (message.type === 'text') return Boolean(caption?.trim());
    return Boolean(caption?.trim());
}

export function useMessageCaption(msg: ChatMessage, isMe: boolean) {
    return useMemo(() => getMessageCaption(msg, isMe, true), [msg.text, msg.type, isMe]);
}
